import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { ThemedText } from '@/components/themed-text';
import { AnimatedView } from '@/components/ui/animated';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useFormFooterInset } from '@/components/mobile-bottom-nav';
import { cn } from '@/utils/cn';

export interface ServicePreview {
  name: string;
  description: string;
  priceLabel: string;
  unitLabel: string;
  bullets: string[];
  ctaLabel: string;
  /** Quanto entra por mês por assinante; ausente no avulso. */
  monthlyPerSubscriber?: number;
}

const SUBSCRIBER_OPTIONS = [5, 10, 25, 50];

const brl = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

interface Props {
  /** Linha curta sempre visível: preço · cadência · duração. */
  summary: string;
  preview: ServicePreview;
  actionLabel: string;
  onAction: () => void;
  actionTestID?: string;
  loading?: boolean;
  onBack?: () => void;
}

/**
 * Doca de prévia: o contrato ao vivo.
 *
 * O prestador está descrevendo o que vai vender, e a única leitura que importa
 * é a do cliente — então ela fica presa no rodapé, resumida numa linha, e abre
 * inteira quando ele quiser conferir.
 */
export function PreviewDock({
  summary,
  preview,
  actionLabel,
  onAction,
  actionTestID = 'service-wizard-action',
  loading,
  onBack,
}: Props) {
  const [open, setOpen] = useState(false);
  const bottomInset = useFormFooterInset();

  return (
    <View
      className="border-t border-neutral-200 bg-card px-4 pt-2"
      style={{ paddingBottom: 16 + bottomInset }}
    >
      <PressableScale
        haptic
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel="Prévia do que o cliente vê"
        onPress={() => setOpen((value) => !value)}
        testID="service-wizard-preview-toggle"
        className="h-12 flex-row items-center gap-3"
      >
        <View className="flex-1">
          <ThemedText className="font-semibold text-[10px] uppercase tracking-wide text-neutral-400">
            O cliente vai ver
          </ThemedText>
          <ThemedText numberOfLines={1} className="font-semibold text-sm text-neutral-900">
            {summary}
          </ThemedText>
        </View>
        <Ionicons className="text-neutral-400" name={open ? 'chevron-down' : 'chevron-up'} size={16} />
      </PressableScale>

      <PreviewBody open={open} preview={preview} />

      <View className="mt-2 flex-row gap-2">
        {onBack ? (
          <PressableScale
            haptic
            accessibilityRole="button"
            onPress={onBack}
            className="h-12 items-center justify-center rounded-full bg-neutral-100 px-5"
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText className="font-semibold text-sm text-neutral-700">Voltar</ThemedText>
          </PressableScale>
        ) : null}
        <PressableScale
          haptic
          accessibilityRole="button"
          disabled={loading}
          onPress={onAction}
          testID={actionTestID}
          className="h-12 flex-1 items-center justify-center rounded-full bg-primary"
          style={{ borderCurve: 'continuous', opacity: loading ? 0.5 : 1 }}
        >
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <ThemedText className="font-bold text-sm text-white">{actionLabel}</ThemedText>
          )}
        </PressableScale>
      </View>
    </View>
  );
}

function PreviewBody({ open, preview }: { open: boolean; preview: ServicePreview }) {
  const [contentHeight, setContentHeight] = useState(0);
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withTiming(open ? 1 : 0, {
      duration: open ? 260 : 220,
      easing: Easing.inOut(Easing.ease),
    });
  }, [open, progress]);

  const collapseStyle = useAnimatedStyle(() => ({
    height: progress.value * contentHeight,
    opacity: progress.value,
  }));

  return (
    <AnimatedView style={[{ overflow: 'hidden' }, collapseStyle]}>
      <View
        style={{ position: 'absolute', left: 0, right: 0 }}
        onLayout={(event) => {
          const height = event.nativeEvent.layout.height;
          if (height > 0 && Math.abs(height - contentHeight) > 0.5) {
            setContentHeight(height);
          }
        }}
      >
        <PreviewCard preview={preview} />
        {preview.monthlyPerSubscriber ? (
          <RevenueProjection monthly={preview.monthlyPerSubscriber} />
        ) : null}
      </View>
    </AnimatedView>
  );
}

function PreviewCard({ preview }: { preview: ServicePreview }) {
  return (
    <View
      className="overflow-hidden rounded-2xl border border-neutral-900/5 bg-card"
      style={{ borderCurve: 'continuous' }}
    >
      <LinearGradient
        colors={['#FD5001', '#FF9A6B']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ height: 56 }}
      />
      <View className="gap-3 p-4">
        <View className="gap-1">
          <ThemedText className="font-semibold text-base text-neutral-900">
            {preview.name}
          </ThemedText>
          {preview.description ? (
            <ThemedText numberOfLines={3} className="text-xs leading-4 text-neutral-500">
              {preview.description}
            </ThemedText>
          ) : null}
        </View>

        <View className="flex-row items-baseline gap-1">
          <ThemedText className="font-bold text-2xl text-neutral-900">
            {preview.priceLabel}
          </ThemedText>
          {preview.unitLabel ? (
            <ThemedText className="font-medium text-xs text-neutral-400">
              {preview.unitLabel}
            </ThemedText>
          ) : null}
        </View>

        <View className="gap-2 border-t border-neutral-100 pt-3">
          {preview.bullets.map((bullet) => (
            <View key={bullet} className="flex-row gap-2">
              <Ionicons className="text-emerald-700" name="checkmark" size={14} />
              <ThemedText className="flex-1 text-xs leading-4 text-neutral-600">
                {bullet}
              </ThemedText>
            </View>
          ))}
        </View>

        <View
          className="h-11 items-center justify-center rounded-full bg-primary"
          style={{ borderCurve: 'continuous' }}
        >
          <ThemedText className="font-bold text-sm text-white">{preview.ctaLabel}</ThemedText>
        </View>
      </View>
    </View>
  );
}

/** Quanto o plano rende com N assinantes — a conta que ninguém faz na hora. */
function RevenueProjection({ monthly }: { monthly: number }) {
  const [subscribers, setSubscribers] = useState(10);

  return (
    <View
      className="mt-3 gap-2 rounded-2xl bg-success-50 p-4"
      style={{ borderCurve: 'continuous' }}
    >
      <View className="flex-row items-end justify-between gap-3">
        <ThemedText className="flex-1 text-xs leading-4 text-success-600">
          Com {subscribers} assinantes você recebe por mês
        </ThemedText>
        <ThemedText className="font-bold text-xl text-success-600">
          {brl(monthly * subscribers)}
        </ThemedText>
      </View>
      <View className="flex-row gap-2">
        {SUBSCRIBER_OPTIONS.map((option) => (
          <PressableScale
            key={option}
            haptic
            accessibilityRole="button"
            accessibilityState={{ selected: option === subscribers }}
            onPress={() => setSubscribers(option)}
            className={cn(
              'h-9 flex-1 items-center justify-center rounded-full',
              option === subscribers ? 'bg-success-600' : 'bg-card',
            )}
            style={{ borderCurve: 'continuous' }}
          >
            <ThemedText
              className={cn(
                'font-semibold text-[13px]',
                option === subscribers ? 'text-white' : 'text-neutral-600',
              )}
            >
              {option}
            </ThemedText>
          </PressableScale>
        ))}
      </View>
    </View>
  );
}
